import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getCandleData,
  getTechnicalIndicator,
  TechnicalIndicatorData,
} from '../../api/metrics';
import { getDefaultDateRange } from '../../utils/dateRangeDefaults';
import CandlestickChart from './CandlestickChart';

/**
 * MA-overlay chart for the Moving Average Crossover strategy detail view (REQ-013).
 *
 * Fetches REAL candle + fast/slow moving-average series for the trade group's
 * product/granularity (no fake/mock data — the same /v1/candles and
 * /v1/indicators endpoints the main chart uses) and renders them through the
 * shared CandlestickChart with the fast and slow MA drawn as overlays. Bullish /
 * bearish crossover points are detected from the real MA series and listed below
 * the chart as markers; the configured trailing-stop distance is surfaced as a
 * reference line relative to the most-recent close.
 *
 * Sibling: the Ichimoku overlay rendering in ChartView/CandlestickChart. This is
 * a self-contained, read-only presentational component — it owns its data fetch
 * and passes the result into the shared chart.
 */
interface MaCrossoverChartProps {
  productId: string;
  granularityMins: number;
  maType: 'SMA' | 'EMA';
  fastPeriod: number;
  slowPeriod: number;
  /** Initial trailing-stop distance as a fraction (e.g. 0.08 = 8%). Optional. */
  trailingStopPct?: number;
}

const FAST_MA_COLOR = '#22d3ee'; // cyan — fast MA
const SLOW_MA_COLOR = '#f59e0b'; // amber — slow MA

interface CrossoverMarker {
  time: string; // ISO-8601
  kind: 'BULLISH' | 'BEARISH';
  fast: number;
  slow: number;
}

/**
 * Detect crossover transitions from two aligned MA series. A bullish crossover is
 * the first bar where fast > slow after fast <= slow; bearish is the inverse.
 */
function detectCrossovers(
  fast: TechnicalIndicatorData[],
  slow: TechnicalIndicatorData[],
): CrossoverMarker[] {
  const slowByTime = new Map<string, number>();
  for (const s of slow) {
    if (s.complete) slowByTime.set(s.metricTime, Number(s.value));
  }
  const aligned = fast
    .filter((f) => f.complete && slowByTime.has(f.metricTime))
    .map((f) => ({
      time: f.metricTime,
      fast: Number(f.value),
      slow: slowByTime.get(f.metricTime) as number,
    }))
    .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

  const markers: CrossoverMarker[] = [];
  for (let i = 1; i < aligned.length; i++) {
    const prev = aligned[i - 1];
    const cur = aligned[i];
    if (prev.fast <= prev.slow && cur.fast > cur.slow) {
      markers.push({ time: cur.time, kind: 'BULLISH', fast: cur.fast, slow: cur.slow });
    } else if (prev.fast >= prev.slow && cur.fast < cur.slow) {
      markers.push({ time: cur.time, kind: 'BEARISH', fast: cur.fast, slow: cur.slow });
    }
  }
  return markers;
}

const MaCrossoverChart: React.FC<MaCrossoverChartProps> = ({
  productId,
  granularityMins,
  maType,
  fastPeriod,
  slowPeriod,
  trailingStopPct,
}) => {
  const dateRange = useMemo(() => getDefaultDateRange(granularityMins), [granularityMins]);

  const { data: candleResponse, isLoading: candlesLoading } = useQuery({
    queryKey: ['ma-crossover-candles', productId, granularityMins, dateRange.startTime, dateRange.endTime],
    queryFn: () => getCandleData(productId, granularityMins, dateRange.startTime, dateRange.endTime),
    enabled: !!productId,
    staleTime: 60000,
  });

  const { data: fastResponse, isLoading: fastLoading } = useQuery({
    queryKey: ['ma-crossover-fast', productId, granularityMins, maType, fastPeriod, dateRange.startTime, dateRange.endTime],
    queryFn: () => getTechnicalIndicator(productId, granularityMins, maType, fastPeriod, dateRange.startTime, dateRange.endTime),
    enabled: !!productId,
    staleTime: 60000,
  });

  const { data: slowResponse, isLoading: slowLoading } = useQuery({
    queryKey: ['ma-crossover-slow', productId, granularityMins, maType, slowPeriod, dateRange.startTime, dateRange.endTime],
    queryFn: () => getTechnicalIndicator(productId, granularityMins, maType, slowPeriod, dateRange.startTime, dateRange.endTime),
    enabled: !!productId,
    staleTime: 60000,
  });

  const loading = candlesLoading || fastLoading || slowLoading;

  const indicators: TechnicalIndicatorData[] = useMemo(
    () => [...(fastResponse?.indicators ?? []), ...(slowResponse?.indicators ?? [])],
    [fastResponse, slowResponse],
  );

  const movingAverages = useMemo(
    () => [
      { type: maType, period: fastPeriod, color: FAST_MA_COLOR, enabled: true },
      { type: maType, period: slowPeriod, color: SLOW_MA_COLOR, enabled: true },
    ],
    [maType, fastPeriod, slowPeriod],
  );

  const crossovers = useMemo(
    () => detectCrossovers(fastResponse?.indicators ?? [], slowResponse?.indicators ?? []),
    [fastResponse, slowResponse],
  );

  const lastClose = candleResponse?.candles?.length
    ? candleResponse.candles[candleResponse.candles.length - 1].close
    : null;
  const trailingStopLevel =
    lastClose != null && trailingStopPct != null && trailingStopPct > 0
      ? lastClose * (1 - trailingStopPct)
      : null;

  return (
    <div className="ma-crossover-chart">
      <CandlestickChart
        data={candleResponse?.candles ?? []}
        loading={loading}
        granularityMins={granularityMins}
        indicators={indicators}
        movingAverages={movingAverages}
        productId={productId}
      />

      <div className="ma-crossover-chart-legend" style={{ marginTop: '0.75rem', display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '12px' }}>
        <span style={{ color: FAST_MA_COLOR }}>● Fast {maType}({fastPeriod})</span>
        <span style={{ color: SLOW_MA_COLOR }}>● Slow {maType}({slowPeriod})</span>
        {trailingStopLevel != null && (
          <span style={{ color: '#ef4444' }}>
            ▸ Trailing stop ≈ {trailingStopLevel.toFixed(productId === 'ADA-USDC' ? 4 : 2)}
            {' '}({(trailingStopPct! * 100).toFixed(1)}% below last close)
          </span>
        )}
      </div>

      <div className="ma-crossover-markers" style={{ marginTop: '0.75rem' }}>
        <h5 style={{ margin: '0 0 0.5rem 0', color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>
          Detected Crossovers ({crossovers.length})
        </h5>
        {crossovers.length === 0 ? (
          <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
            No fast/slow MA crossovers in the visible window.
          </small>
        ) : (
          <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '11px', color: 'rgba(255,255,255,0.7)' }}>
            {crossovers.slice(-8).reverse().map((m) => (
              <li key={`${m.kind}-${m.time}`} style={{ color: m.kind === 'BULLISH' ? '#10b981' : '#ef4444' }}>
                {m.kind === 'BULLISH' ? '▲ Bullish' : '▼ Bearish'} crossover @{' '}
                {new Date(m.time).toLocaleString()} (fast {m.fast.toFixed(2)} / slow {m.slow.toFixed(2)})
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default MaCrossoverChart;
